/* istanbul ignore file */
import React, {
  useContext, useEffect, useMemo, useRef, useState
} from 'react';
import { useRouter } from 'next/router';
import { v4 as uuidv4 } from 'uuid';
import analyticsTrack from '@lib/analytics/analyticsTrack';
import getProductVanityPath from '@lib/vendorVariant/getProductVanityPath';
import UserContext from '@context/User/user-context';
import ShopNavigationMenu, {
  IShopNavigationMenu,
} from '@components/shared/shop/ShopNavigationMenu';
import ProductBreadcrumbs
  from '@components/pages/ProductV2/shared/components/ProductBreadcrumbs/ProductBreadcrumbs';
import If from '@components/shared/conditional/If/If';
import AddToBoardModalContext from '@context/Modal/add-to-board-modal-context';
import boardItemTypes from '@constants/boardItemTypes';
import ShowAt from '@components/shared/conditional/ShowAt';
import { useTheme } from '@components/theme';
import OurStories from '@components/shared/OurStories/OurStories';
import {
  IVendorVariantInternal,
} from '@models/VendorVariant/i-vendor-variant';
import DefaultTemplate from '@components//shared/templates/DefaultTemplate/DefaultTemplate';
import VendorVariantContext from '@context/VendorVariant/vendor-variant-context';
import {
  ICollectionWithProductCards,
} from '@components/pages/ShopLanding/model/collectionWithProductCards';
import { userIsAdmin } from '@lib/auth/auth';
import HideAt from '@components/shared/conditional/HideAt';
import useCartActions from '@hooks/User/useAddToCartActions';
import getBoardIdFromRouter from '@components/pages/ProductV2/shared/utils/getBoardIdFromRouter';
import AddedToCartConfirmationModalContext from '@context/Modal/added-to-cart-confirmation-modal-context';
import Error from '../../Error/Error';
import ProductDetails from '../shared/components/ProductDetails/ProductDetails';
import FeaturedDesigns from '../shared/components/FeaturedDesigns/FeaturedDesigns';
import RecommendedProducts from '../shared/components/RecommendedProducts/RecommendedProducts';
import Reviews from '../shared/components/Reviews/Reviews';
import Footer from '../shared/components/Footer/Footer';
import CollectionWithProducts
  from '../shared/components/CollectionWithProducts/CollectionWithProducts';
import getVendorVariantAnalyticsPayload from '../shared/utils/getVendorVariantAnalyticsPayload';
import prepareAlternativesForAnalytics
  from '../../../shared/providers/BoardBuilderModalProvider/helpers/prepareAlternativesForAnalytics';
import buildJsonSchema from '../shared/utils/buildJsonSchema';
import styles from './PageProducts.module.scss';
import StickyHeader from '../shared/components/StickyHeader/StickyHeader';
import { createFacebookOpenGraph } from '../shared/utils/tracking';
import MobileImageZoom, { IZoomableImage } from '../shared/components/MobileImageZoom/MobileImageZoom';

interface IPageProductProps {
  shopNavigationMenu: IShopNavigationMenu[];
  collectionWithProducts: Omit<ICollectionWithProductCards, 'vendorVariants'>;
  isHidden?: boolean;
  vendorVariant: IVendorVariantInternal | null;
  similarData: any;
  completeTheLookData: IVendorVariantInternal[];
  host: string;
  isIosNative: boolean;
  canonicalUrl: string;
  noIndex: boolean;
}

const PageProduct = ({
  isHidden,
  vendorVariant,
  similarData,
  completeTheLookData,
  host,
  isIosNative,
  canonicalUrl,
  shopNavigationMenu,
  collectionWithProducts,
  noIndex,
}: IPageProductProps) => {
  const router = useRouter();
  const theme = useTheme();

  const { user } = useContext(UserContext);
  const { open } = useContext(AddToBoardModalContext);

  const [unbounceBannerHeight, setUnbounceBannerHeight] = useState(0);
  const [analyticsListId, setAnalyticsListId] = useState('');
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const [activeZoomImage, setActiveZoomImage] = useState<IZoomableImage | null>(null);
  const reviewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isHidden && userIsAdmin(user)) {
      alert('Warning - this product has either been hidden from customers, is an undesirable commission tier, or the vendor is DO NOT USE'); // eslint-disable-line
    }
  }, []);

  /* Tracking */
  useEffect(() => {
    if (vendorVariant) {
      analyticsTrack('product_viewed', {
        type: 'pdp',
        listId: analyticsListId,
        ...getVendorVariantAnalyticsPayload(vendorVariant),
      });
    }
  }, [vendorVariant]);

  useEffect(() => {
    if (similarData?.similarVendorVariants?.items?.length) {
      const listId = uuidv4();

      setAnalyticsListId(listId);

      analyticsTrack(
        'product_list_viewed',
        {
          listId,
          listType: 'product_alternate',
          products: prepareAlternativesForAnalytics(similarData?.similarVendorVariants?.items),
          vvId: vendorVariant?.id,
          vvName: vendorVariant?.title,
        },
      );
    }
  }, [similarData]);
  /* Tracking */

  if (!vendorVariant) return <Error data-test="error-organism" message="Unable to fetch Vendor Variant" />;

  const {
    description,
    images,
    title,
    vendor,
    retailPrice,
    currentPrice,
    feedCreatorId,
    taxonomy,
    blogPosts,
  } = vendorVariant;

  const vanityPath = useMemo(() => getProductVanityPath(vendorVariant), [vendorVariant]);
  const vendorVariantId = useMemo(() => vendorVariant.id.toString(), [vendorVariant.id]);
  const retailPriceNum = useMemo(() => retailPrice?.price ?? undefined, [retailPrice]);

  const isCartable = useMemo(() => {
    return retailPriceNum !== undefined;
  }, [retailPriceNum]);

  const handleSave = () => {
    open({
      saveItemImageUrl: vendorVariant.images[0]?.uri,
      saveItemId: vendorVariant.id,
      saveItemType: boardItemTypes.VENDOR_VARIANTS,
      source: 'full_page_pdp',
    });
  };

  const handleScrollToReview = () => {
    if (reviewRef.current) {
      reviewRef.current.scrollIntoView({ behavior: 'smooth', inline: 'start' });
    }
  };

  const [triggeredAddToCart, setTriggeredAddToCart] = useState(false);
  const { open: openAddedToCartConfirmation } = useContext(AddedToCartConfirmationModalContext);

  const { getCartActionsComponent, isDoneAddingToCart, quantity } =
    useCartActions(
      vendorVariant,
      getBoardIdFromRouter(router, user!),
      !isCartable,
      setTriggeredAddToCart
    );

  useEffect(() => {
    if (triggeredAddToCart && isDoneAddingToCart) {
      openAddedToCartConfirmation({ vendorVariant, quantity });
      setTriggeredAddToCart(false);
    }
  }, [triggeredAddToCart, isDoneAddingToCart]);

  return (
    <VendorVariantContext.Provider value={vendorVariant}>
      {activeZoomImage ? (
        <MobileImageZoom
          activeImage={activeZoomImage}
          onClose={() => setActiveZoomImage(null)}
          onImageSelected={(image) => setActiveZoomImage(image)}
        />
      ) : (
        <DefaultTemplate
          analyticsPageProperties={{
            ecomm_prodid: vendorVariantId,
            ecomm_pagetype: 'product',
            ecomm_totalvalue: currentPrice,
            ecomm_category: taxonomy.title,
            dynx_itemid: vendorVariantId,
            page_version: 'v2'
          }}
          data-test="product__template"
          title={`${title} - ${vendor.name} | Havenly`}
          description={`Design your space with ${title} on Havenly.com with real interior designers`}
          twitter={{
            card: 'summary_large_image',
            site: '@thehavenly',
          }}
          facebook={createFacebookOpenGraph(
            images,
            retailPriceNum,
            currentPrice,
            `${title} - ${vendor.name} | Havenly`,
            description,
            `${host}${router.asPath}`,
          )}
          canonicalLink={!feedCreatorId ? undefined : canonicalUrl}
          isIosNative={isIosNative}
          noIndex={!feedCreatorId || noIndex}
          scripts={['/static/affirm.js']}
          jsonSchema={buildJsonSchema(vendorVariant, vanityPath)}
          checkUnbounceBannerHeight={
            (height) => setUnbounceBannerHeight(height)
          }
          className={theme.styles.Body}
        >
          <div className={styles.PageWrapper}>
            <ShopNavigationMenu
              shopNavigationMenu={shopNavigationMenu}
              unbounceBannerHeight={unbounceBannerHeight}
            />
            <ShowAt breakpoint="desktop">
              <StickyHeader
                cartActions={getCartActionsComponent()}
                showStickyHeader={showStickyHeader}
                handleScrollToReview={handleScrollToReview}
                unbounceBannerHeight={unbounceBannerHeight}
              />
            </ShowAt>
            <div
              className={styles.ContentContainer}
              data-test="product"
            >
              <If condition={!!vendorVariant.vendor}>
                <ProductBreadcrumbs
                  vendor={vendorVariant.vendor}
                  vendorVariantId={vendorVariant.id.toString()}
                  merchGroupCategory={vendorVariant.merchGroupTaxonomy}
                  className={theme.styles.Caption}
                />
              </If>
              <ProductDetails
                cartActions={getCartActionsComponent()}
                setShowStickyHeader={(state: boolean) => setShowStickyHeader(state)}
                setActiveZoomImage={(image) => setActiveZoomImage(image)}
                onClickReviews={handleScrollToReview}
                handleSave={handleSave}
              />
              <RecommendedProducts
                sectionTitle={'Complete the Look'}
                analyticsListId={analyticsListId}
                recommendedProducts={completeTheLookData}
              />
              <RecommendedProducts
                sectionTitle={'You may also love these'}
                sectionSubTitle={'Our designers recommend these alternatives.'}
                analyticsListId={analyticsListId}
                recommendedProducts={similarData?.similarVendorVariants?.items || []}
              />
              <FeaturedDesigns
                vendorVariantIds={[vendorVariant.id]}
              />
              <CollectionWithProducts {...collectionWithProducts} />
              <If condition={blogPosts?.length > 0}>
                <OurStories
                  className={styles.OurStories}
                  blogLinks={blogPosts.map(({ imageUrl, title: postTitle, url }) => ({
                    image: imageUrl,
                    description: postTitle,
                    url
                  }))}
                />
              </If>

              {vendorVariant.reviewStats.count > 0 && (
                <Reviews
                  className={styles.Reviews}
                  summary={vendorVariant.reviewStats}
                  reviews={vendorVariant.reviews}
                  reviewRef={reviewRef}
                />
              )}
              <Footer />
              <HideAt breakpoint="desktop">
                {getCartActionsComponent(true, true)}
              </HideAt>
            </div>
          </div>
        </DefaultTemplate>
      )}
    </VendorVariantContext.Provider>
  );
};

export default PageProduct;
